/**
 * GTD Domain Seeder
 * 
 * Implements the DomainSeederService pattern for GTD (Getting Things Done) domain.
 * Seeds inbox items, projects, next actions, and reference materials following
 * David Allen's GTD methodology.
 */

import { BaseDomainSeeder, DomainSeederService } from './shared-seeding/BaseDomainSeeder';
import { GTD_COLLECTIONS } from '../config/collections';
import { GTDDataSeeder } from '../utils/seed-gtd-data';
import { getMongoClient, getMongoConfig } from "@swoft/persistence";

export class GtdDomainSeeder extends BaseDomainSeeder {
  constructor() {
    super('gtd-domain');
  }

  async seedDomain(_organizationId: string, _projectId: string): Promise<{
    success: boolean;
    metrics: {
      totalCollections: number;
      totalDocuments: number;
      validDocuments: number;
      invalidDocuments: number;
      seedingDurationMs: number;
      validationDurationMs: number;
    };
    error?: string;
  }> {
    const seedingStartTime = Date.now();
    
    try {
      console.log(`🧠 Starting ${this.domainName} domain seeding...`);
      
      // Create and run the real GTD data seeder
      const gtdSeeder = new GTDDataSeeder();
      await gtdSeeder.run();
      
      const seedingDurationMs = Date.now() - seedingStartTime;
      
      // Perform validation
      const validationStartTime = Date.now();
      const validationResult = await this.validateSeededData();
      const validationDurationMs = Date.now() - validationStartTime;
      
      console.log(`✅ Successfully seeded ${this.domainName} domain`);
      
      return this.createSuccessResponse({
        totalCollections: 5, // inbox_items, projects, next_actions, reference_items, someday_maybe
        totalDocuments: validationResult.totalDocuments,
        validDocuments: validationResult.validDocuments,
        invalidDocuments: validationResult.invalidDocuments,
        seedingDurationMs,
        validationDurationMs
      });
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`❌ Domain seeding failed for ${this.domainName}:`, errorMessage);
      
      return this.createErrorResponse(errorMessage, {
        totalCollections: 0,
        totalDocuments: 0,
        validDocuments: 0,
        invalidDocuments: 0,
        seedingDurationMs: Date.now() - seedingStartTime,
        validationDurationMs: 0
      });
    }
  }

  async validateSeededData(): Promise<{
    isValid: boolean;
    totalDocuments: number;
    validDocuments: number;
    invalidDocuments: number;
    errors: string[];
  }> {
    try {
      const mongoClient = getMongoClient();
      const config = getMongoConfig();
      const db = mongoClient.db(config?.dbName);
      
      const personId = '52149abb-d080-4b1c-8283-8d603b9ca44d'; // Derick's person ID
      const collections = Object.values(GTD_COLLECTIONS);
      
      let totalDocuments = 0;
      let validDocuments = 0;
      let invalidDocuments = 0;
      const errors: string[] = [];
      
      for (const collectionName of collections) {
        try {
          const coll = db.collection(collectionName);
          let docs;
          
          // Filter by appropriate field based on collection
          switch (collectionName) {
            case GTD_COLLECTIONS.INBOX_ITEMS:
              docs = await coll.find({ capturedByPersonId: personId }).toArray();
              break;
            case GTD_COLLECTIONS.PROJECTS:
              docs = await coll.find({ ownerId: personId }).toArray();
              break;
            case GTD_COLLECTIONS.NEXT_ACTIONS:
              docs = await coll.find({ assignedToPersonId: personId }).toArray();
              break;
            case GTD_COLLECTIONS.REFERENCE_ITEMS:
              docs = await coll.find({ ownerId: personId }).toArray();
              break;
            case GTD_COLLECTIONS.SOMEDAY_MAYBE:
              docs = await coll.find({ personId }).toArray();
              break;
            default:
              docs = await coll.find({}).toArray();
          }
          
          totalDocuments += docs.length;
          
          for (const doc of docs) {
            const validation = this.validateDocument(doc, collectionName);
            if (validation.isValid) {
              validDocuments++;
            } else {
              invalidDocuments++;
              errors.push(...validation.errors);
            }
          }
          
          console.log(`✅ Validated ${collectionName}: ${docs.length} documents`);
        } catch (error) {
          errors.push(`Failed to validate ${collectionName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }
      
      return {
        isValid: errors.length === 0,
        totalDocuments,
        validDocuments,
        invalidDocuments,
        errors
      };
      
    } catch (error) {
      return {
        isValid: false,
        totalDocuments: 0,
        validDocuments: 0,
        invalidDocuments: 0,
        errors: [error instanceof Error ? error.message : 'Unknown validation error']
      };
    }
  }

  private validateDocument(doc: any, collectionName: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Basic validation - ensure required fields exist
    if (!doc.id) {
      errors.push(`Document in ${collectionName} missing required field: id`);
    }

    // Collection-specific validation
    switch (collectionName) {
      case GTD_COLLECTIONS.INBOX_ITEMS:
        if (!doc.itemId || !doc.content || !doc.capturedByPersonId || !doc.capturedAt) {
          errors.push(`Inbox item document missing required fields`);
        }
        break;

      case GTD_COLLECTIONS.PROJECTS:
        if (!doc.projectId || !doc.title || !doc.ownerId || !doc.status) {
          errors.push(`Project document missing required fields`);
        }
        break;

      case GTD_COLLECTIONS.NEXT_ACTIONS:
        if (!doc.actionId || !doc.description || !doc.context || !doc.status) {
          errors.push(`Next action document missing required fields`);
        }
        break;

      case GTD_COLLECTIONS.REFERENCE_ITEMS:
        if (!doc.referenceId || !doc.title || !doc.content || !doc.category) {
          errors.push(`Reference item document missing required fields`);
        }
        break;

      case GTD_COLLECTIONS.SOMEDAY_MAYBE:
        if (!doc.id || !doc.title || !doc.personId) {
          errors.push(`Someday/maybe item document missing required fields`);
        }
        break;
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

/**
 * Factory function to create GTD domain seeder
 */
export function createGtdDomainSeeder(): DomainSeederService {
  return new GtdDomainSeeder();
}